import { useAuth } from "./useAuth";

/**
 * ProtectedRoute — drop-in replacement for techveda-react-auth's ProtectedRoute.
 *
 * Behaviors preserved bit-for-bit from upstream so admin pages behave identically:
 *   - While loading, render `loadingComponent || fallback`
 *   - If not authenticated, `window.location.href = redirectTo` (full page reload)
 *     and render `fallback`
 *   - If `role` is set and user has none of them, redirect to "/unauthorized"
 *   - If `permission` is set and user has none of them, redirect to "/unauthorized"
 *   - Otherwise, render children
 *
 * The full-reload redirect (vs react-router <Navigate>) is intentional — it clears
 * any stale in-memory state when sessions expire.
 */
export const ProtectedRoute = ({
  children,
  role,
  permission,
  redirectTo = "/login",
  fallback = null,
  loadingComponent = null,
}) => {
  const { isAuthenticated, hasRole, hasPermission, loading } = useAuth();

  if (loading) {
    return <>{loadingComponent || fallback}</>;
  }

  if (!isAuthenticated) {
    if (typeof window !== "undefined") {
      window.location.href = redirectTo;
    }
    return <>{fallback}</>;
  }

  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    if (!roles.some((r) => hasRole(r))) {
      if (typeof window !== "undefined") {
        window.location.href = "/unauthorized";
      }
      return <>{fallback}</>;
    }
  }

  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    if (!permissions.some((p) => hasPermission(p))) {
      if (typeof window !== "undefined") {
        window.location.href = "/unauthorized";
      }
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
};
